import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LoadingSpinner from 'component/LoadingSpinner';
import HomeShortcutService from 'service/HomeShortcut/HomeShortcutService';

const HomeShortcutForm: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);
  const navigate = useNavigate();
  const homeShortcutService = HomeShortcutService();

  const [name, setName] = useState('');
  const [type, setType] = useState<'Webview' | 'Stackscreen'>('Webview');
  const [value, setValue] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [sort, setSort] = useState<number>(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const MAX_SIZE = 100 * 1024;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoadingSave(true);
    try {
      let shortcutId: string | undefined = id;

      if (id === "0") {
        const result = await homeShortcutCreate();
        shortcutId = result?.data?.id;
        alert(`Data ${name} successfully created!`);
      } else {
        await homeShortcutUpdate();
        alert(`Data ${name} successfully updated!`);
      }

      if (iconFile && shortcutId && shortcutId !== "0") {
        const token = profile?.data.token;
        if (token) {
          await homeShortcutService.uploadIcon(token, shortcutId, iconFile);
        }
      }

      navigate("/mobile-shortcut");
    } catch (err: any) {
      console.error(err);
      const errorMessage = typeof err === 'string' ? err : err.message || 'Terjadi kesalahan saat menyimpan data';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token) {
      alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }
    setIsLoadingDetail(true);
    try {
      const res = await homeShortcutService.getHomeShortcutDetail(token, id ?? "0");
      setName(res.data?.name || '');
      setType(res.data?.type || 'Webview');
      setValue(res.data?.value || '');
      setIconUrl(res.data?.iconUrl || '');
      setPreviewUrl(res.data?.iconUrl || null);
      setIsActive(res.data?.isActive ?? true);
      setSort(res.data?.sort ?? 0);
      setIconFile(null);
    } catch (err: any) {
      console.error(err);
      const errorMessage = typeof err === 'string' ? err : err.message || 'Terjadi kesalahan saat mengambil detail home shortcut';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const homeShortcutCreate = async () => {
    const token = profile?.data.token;
    if (!token) {
      alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      const request = {
        name,
        type,
        value,
        iconUrl: iconUrl || '',
        isActive
      };
      const result = await homeShortcutService.createHomeShortcut(token, request);
      return result;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const homeShortcutUpdate = async () => {
    const token = profile?.data.token;
    if (!token) {
      alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      const request = {
        id: id!,
        name,
        type,
        value,
        iconUrl,
        isActive
      };
      console.log(request);
      await homeShortcutService.updateHomeShortcut(token, request);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const handleFileChange = (file: File | null) => {
    if (!file) {
      setIconFile(null);
      setPreviewUrl(iconUrl || null);
      return;
    }

    if (file.type !== "image/png") {
      alert("Format gambar harus .png");
      return;
    }

    if (file.size > MAX_SIZE) {
      alert("Ukuran file maksimal 100 KB");
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      if (img.width !== img.height) {
        alert("Gambar harus rasio 1:1 (persegi)");
        URL.revokeObjectURL(objectUrl);
        return;
      }

      setIconFile(file);
      setPreviewUrl(objectUrl);
      setIconUrl('');
    };

    img.src = objectUrl;
  };

  const handleTypeChange = (newType: 'Webview' | 'Stackscreen') => {
    setType(newType);
    setValue('');
  };

  useEffect(() => {
    if (id !== "0") fetchDetail();
  }, []);

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">{id === "0" ? "Add Home Shortcut" : "Edit Home Shortcut"}</h4>
        <div className="p-4" style={{ background: 'white' }}>
          {isLoadingDetail ? <LoadingSpinner /> :
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-sm-6">
                  <h5 className="mb-4">Basic Information</h5>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Name</label>
                    <input 
                      type="text" 
                      className="form-control form-control-compact" 
                      value={name} 
                      onChange={e => setName(e.target.value)} 
                      required 
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Type</label>
                    <select 
                      className="form-select" 
                      value={type} 
                      onChange={e => handleTypeChange(e.target.value as 'Webview' | 'Stackscreen')}
                      required
                    >
                      <option value="Webview">Webview</option>
                      <option value="Stackscreen">Stackscreen</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Value</label>
                    <input 
                      type="text" 
                      className="form-control form-control-compact" 
                      value={value} 
                      onChange={e => setValue(e.target.value)} 
                      placeholder={type === 'Webview' ? 'https://example.com' : 'screenName (contoh: TransWallet, EditProfile, FAQ)'}
                      required 
                    />
                  </div>
                </div>

                <div className="col-sm-6">
                  <h5 className="mb-4">Icon & Status</h5>
                  <div className='mb-3'>
                    <label className="form-label fw-semibold">Icon</label>
                    <div className='d-flex gap-3'>
                      {previewUrl ?
                        <img 
                          src={previewUrl} 
                          className='rounded col-auto border' 
                          alt="Icon Preview" 
                          style={{ width: '80px', height: '80px', objectFit: 'cover' }} 
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/80?text=No+Image';
                          }}
                        />
                        :
                        <span className='rounded col-auto border text-center d-flex align-items-center justify-content-center' style={{ width: '80px', height: '80px', background: '#f0f0f0' }}>
                          <i className="fa-solid fa-image text-muted"></i>
                        </span>
                      }
                      <div className='col'>
                        <input 
                          type="file" 
                          className="form-control form-control-compact mb-2" 
                          accept="image/png"
                          onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                        />
                        <small className="text-muted">Format PNG, rasio 1:1, maks 100KB</small>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold d-block">Status</label>
                    <div className="form-check form-switch" style={{ transform: 'scale(1.8)', transformOrigin: 'left center', marginTop: '6px', marginBottom: '6px' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="isActiveSwitch"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                      />
                      <label className="form-check-label ms-1" htmlFor="isActiveSwitch">
                        <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-100 d-flex justify-content-end mt-4">
                <Link to="/mobile-shortcut">
                  <button type="button" className="btn btn-outline-primary">Cancel</button>
                </Link>
                <button type="submit" className="btn btn-primary ms-2" disabled={isLoadingSave}>
                  {isLoadingSave && <LoadingSpinner reverse />}
                  Save
                </button>
              </div>
            </form>
          }
        </div>
      </div>
    </div>
  );
};

export default HomeShortcutForm;